import React, { useEffect, useState } from 'react';
import GridFilterPanel from '../../commons/filters/GridFilterPanel.jsx';
import * as Utils from '../../commons/utils/Utils.js';
import {
	INVENTARIO_STOCK_FILTROS_STORAGE_KEY,
	buildInventarioStockFilterFields,
	getInventarioStockFilterDefaults,
} from './inventarioStockFilterFields.js';
// VEC-3316: la normalización a {value,label} —incluido el cuidado del label nunca undefined, que
// MUI necesita para filtrar— vive ahí, junto con la serialización del par de ubicación.
import { cargarUbicaciones } from './inventarioUbicaciones.js';

/**
 * VEC-3313 / VEC-3781 / VEC-3316 — filtros de la grilla de Stock por Depósito.
 *
 * Este componente NO implementa el panel: lo alimenta. El estado, la persistencia y la
 * serialización del query son de <GridFilterPanel /> (commons/filters/); acá solo se cargan las
 * opciones del desplegable de UBICACIONES y el buscador de ítems.
 *
 * VEC-3316: el desplegable ofrece almacenes Y bases, y la carga y la normalización se delegan a
 * `inventarioUbicaciones.js`, que es el catálogo compartido con el formulario de ingreso y con la
 * sección de reservas del ticket. Antes esa lógica vivía acá con un solo endpoint.
 *
 * Es el molde de `MovilesGridFiltros.jsx`, no el de `InventarioManoObraGridFiltros.jsx`: ese último
 * es el panel escrito a mano, anterior al refactor de VEC-3690 / VEC-3698.
 */

const OPTIONS_INIT = { ubicacionId: [] };

/**
 * VEC-3781 Sc. 04 — el buscador de ítems del filtro de Producto/Repuesto.
 *
 * Es el MISMO endpoint que alimenta el select del formulario de ingreso (VEC-3314 Sc. 01), y por eso
 * este filtro se pudo hacer como desplegable buscable y no como texto: acota por búsqueda y tiene
 * tope propio (20 por defecto, 50 máximo). El `/api/items/simple-search` del legacy no sirve — hace
 * un `get()` sin límite, que es el anti-patrón que documenta grid-filter-panel.md, y es la razón por
 * la que el filtro de ítem del historial global de VEC-3315 todavía es un campo de texto.
 */
const URL_ITEMS_SELECT = '/inventario/items/select';

const InventarioStockGridFiltros = (props) => {
	const [options, setOptions] = useState(OPTIONS_INIT);
	const [optionsLoading, setOptionsLoading] = useState(true);

	/**
	 * El panel llama a esto con el texto tipeado (ya con su debounce y su mínimo de 2 caracteres) y
	 * espera un array de `{value, label}` — que es exactamente lo que devuelve el endpoint, así que
	 * no hay que mapear nada.
	 *
	 * Un fallo de red devuelve lista vacía en vez de propagar: el desplegable muestra "sin
	 * resultados", que es un estado que el usuario entiende, en vez de tirar el render del panel.
	 * Y `hayMas` se ignora a propósito — el backend lo sigue devolviendo (contrato documentado y con
	 * test), pero avisar "hay más ítems" en un filtro repetiría el hallazgo que se cerró en el
	 * formulario de ingreso el 04/09 sacando ese aviso entero.
	 */
	const buscarItems = (busqueda) => {
		const texto = busqueda ? String(busqueda).trim() : '';
		const query = texto ? '?busqueda=' + encodeURIComponent(texto) : '';

		return Utils.getData(URL_ITEMS_SELECT + query)
			.then((data) => (data && data.items) || [])
			.catch(() => []);
	};

	const fields = buildInventarioStockFilterFields({ onSearch: buscarItems });

	/**
	 * Las opciones se avisan hacia arriba además de alimentar al panel: la grilla necesita traducir
	 * el id del depósito filtrado a su nombre para el modal del Scenario 05, y el panel solo entrega
	 * el query ya serializado (el `value`, sin el `label`). Sin esto habría que pedir el mismo select
	 * dos veces, o el modal diría "movimientos en el depósito 6".
	 */
	useEffect(() => {
		setOptionsLoading(true);
		// `cargarUbicaciones()` pide los DOS selects en paralelo y tolera que uno falle: un tenant
		// donde /bases/select responda 403 sigue viendo los almacenes. Nunca rechaza, así que el
		// `.catch` de acá cubre solo un error inesperado del propio then.
		cargarUbicaciones()
			.then((ubicaciones) => {
				const cargadas = { ubicacionId: ubicaciones };
				setOptions(cargadas);
				if (props.onOptionsLoaded) props.onOptionsLoaded(cargadas);
			})
			.catch(() => setOptions(OPTIONS_INIT))
			.finally(() => setOptionsLoading(false));
	}, []);

	return (
		<GridFilterPanel
			open={props.open}
			fields={fields}
			options={options}
			defaults={getInventarioStockFilterDefaults()}
			storageKey={INVENTARIO_STOCK_FILTROS_STORAGE_KEY}
			loading={props.loading || optionsLoading}
			// La grilla es de React/MUI: trae los datos ella misma a partir del query y no tiene de
			// dónde sacarlo al montar. Sin esto, los filtros persistidos se VEN en los controles pero
			// no están aplicados, y el listado sale sin filtrar sin que el usuario lo note.
			applyOnMount={true}
			resetSignal={props.resetSignal}
			onResetSignalHandled={props.onResetSignalHandled}
			onApply={props.onApply}
		/>
	);
};

export default InventarioStockGridFiltros;
